import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { CheckCircle2, AlertCircle, Clock, Send } from "lucide-react";

// V1.55 · Módulo 5 — Solicitud Permiso B / Psicotécnico (página pública).
// Campos automáticos: Permiso B, transmisión manual, idioma castellano.
// Firma grande y legible sobre box BLANCO (para verificarla contra el DNI).

function BigSignatureCanvas({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: ((t ? t.clientX : e.clientX) - rect.left) * (canvasRef.current.width / rect.width),
      y: ((t ? t.clientY : e.clientY) - rect.top) * (canvasRef.current.height / rect.height),
    };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = getPos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 5; // trazo grueso ×2 para máxima legibilidad
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (isEmpty) setIsEmpty(false);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (canvasRef.current) onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    setIsEmpty(true);
    onChange("");
  };
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = c.clientWidth * dpr;
    c.height = c.clientHeight * dpr;
    // Box BLANCO — la firma exportada queda sobre fondo blanco puro
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  return (
    <div>
      <div className="relative rounded-xl border-2 border-neutral-300 bg-white overflow-hidden shadow-inner">
        <canvas
          ref={canvasRef}
          data-testid="psico-signature-canvas"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className="block w-full h-64 touch-none cursor-crosshair"
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-neutral-400 text-sm font-medium">
            Firma aquí, grande y clara (como en tu DNI)
          </div>
        )}
      </div>
      <div className="flex justify-end mt-2">
        <button type="button" onClick={clear} data-testid="psico-signature-clear"
          className="text-xs font-semibold text-neutral-500 hover:text-red-600">
          Borrar firma
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold mb-1 block text-neutral-700">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500";

export default function PsicotecnicoPage() {
  useDocumentTitle("Solicitud Permiso B · Natur Autoescuela");
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("pending");
  const [centers, setCenters] = useState([]);
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    nombre: "", primer_apellido: "", segundo_apellido: "", dni: "",
    sexo: "", tramite: "Permiso B", tasa: "",
    transmision: "manual", idioma: "castellano",
    restricciones: "",
    centro_reconocimiento_id: "", centro_reconocimiento_codigo: "",
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/psicotecnico/${token}`);
        setStatus(data.status);
        setCenters(data.centers || []);
        if (data.prefill) {
          setForm((f) => ({
            ...f,
            nombre: data.prefill.nombre || "",
            primer_apellido: data.prefill.primer_apellido || "",
            segundo_apellido: data.prefill.segundo_apellido || "",
            dni: data.prefill.dni || "",
            tasa: data.prefill.tasa || "",
          }));
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "No se pudo cargar el enlace.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sexo) { toast.error("Indica si eres hombre o mujer"); return; }
    if (!signature) { toast.error("Falta tu firma"); return; }
    setSubmitting(true);
    try {
      await api.post(`/public/psicotecnico/${token}/submit`, { data: form, signature_b64: signature });
      setStatus("completed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-md space-y-4" data-testid="psico-page">
        <div className="flex flex-col items-center">
          <Logo variant="card" />
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mt-3 text-center">
            Solicitud Permiso B · Psicotécnico
          </div>
        </div>

        {loading && <p className="text-center text-neutral-500 py-8">Cargando…</p>}

        {!loading && error && (
          <div className="card-surface p-6 text-center" data-testid="psico-error">
            <AlertCircle className="h-10 w-10 text-red-600 mx-auto mb-2" />
            <div className="font-bold">No se pudo cargar</div>
            <p className="text-sm text-neutral-500 mt-1">{String(error)}</p>
          </div>
        )}

        {!loading && !error && status === "completed" && (
          <div className="card-surface p-6 text-center" data-testid="psico-done">
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-2" />
            <div className="text-lg font-bold text-green-800">Solicitud enviada</div>
            <p className="text-sm text-neutral-600 mt-1">Gracias, hemos recibido tus datos y tu firma.</p>
          </div>
        )}

        {!loading && !error && (status === "expired" || status === "cancelled") && (
          <div className="card-surface p-6 text-center" data-testid="psico-inactive">
            <Clock className="h-10 w-10 text-neutral-400 mx-auto mb-2" />
            <div className="font-bold">Este enlace ya no está activo</div>
            <p className="text-sm text-neutral-500 mt-1">Pídenos otro y te lo enviaremos inmediatamente.</p>
          </div>
        )}

        {!loading && !error && status === "pending" && (
          <form onSubmit={submit} className="space-y-4">
            <div className="card-surface p-4 space-y-3">
              <div className="text-sm font-bold">1 · Tus datos</div>
              <div className="grid grid-cols-1 gap-3">
                <Field label="Nombre"><input className={inputCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} required data-testid="psico-nombre" /></Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="1º apellido"><input className={inputCls} value={form.primer_apellido} onChange={(e) => set({ primer_apellido: e.target.value })} data-testid="psico-apellido1" /></Field>
                  <Field label="2º apellido"><input className={inputCls} value={form.segundo_apellido} onChange={(e) => set({ segundo_apellido: e.target.value })} data-testid="psico-apellido2" /></Field>
                </div>
                <Field label="DNI / NIE"><input className={inputCls} value={form.dni} onChange={(e) => set({ dni: e.target.value })} required data-testid="psico-dni" /></Field>
                <Field label="Sexo">
                  <div className="flex gap-2">
                    {["Hombre", "Mujer"].map((s) => (
                      <button key={s} type="button" onClick={() => set({ sexo: s })} data-testid={`psico-sexo-${s.toLowerCase()}`}
                        className={"flex-1 h-10 rounded-lg border text-sm font-semibold transition-colors " + (form.sexo === s ? "border-orange-400 bg-orange-50 text-orange-800" : "border-neutral-300 bg-white text-neutral-600")}>
                        {form.sexo === s ? "☑ " : "☐ "}{s}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
            </div>

            <div className="card-surface p-4 space-y-3">
              <div className="text-sm font-bold">2 · Trámite que deseas realizar</div>
              <Field label="Trámite">
                <select className={inputCls} value={form.tramite} onChange={(e) => set({ tramite: e.target.value })} data-testid="psico-tramite">
                  <option value="Permiso B">Permiso B</option>
                  <option value="Otro">Otro (indicar a la autoescuela)</option>
                </select>
              </Field>
              <Field label="TASA (se añade automáticamente a la solicitud)">
                <input className={inputCls + " font-mono"} value={form.tasa} onChange={(e) => set({ tasa: e.target.value })} placeholder="Nº de tasa DGT" data-testid="psico-tasa" />
              </Field>
              <div className="rounded-lg bg-neutral-50 border border-neutral-200 p-2.5 text-xs text-neutral-600 space-y-0.5" data-testid="psico-autos">
                <div>☑ Clase de permiso: <strong>B</strong> (automático)</div>
                <div>☑ Transmisión: <strong>manual</strong> (automático)</div>
                <div>☑ Idioma: <strong>castellano</strong> (automático)</div>
              </div>
              <Field label="Selecciones restrictivas (si procede)">
                <input className={inputCls} value={form.restricciones} onChange={(e) => set({ restricciones: e.target.value })} placeholder="Ej: lentes correctoras…" data-testid="psico-restricciones" />
              </Field>
            </div>

            <div className="card-surface p-4 space-y-3">
              <div className="text-sm font-bold">3 · Centro de reconocimiento</div>
              <Field label="Centro">
                <select className={inputCls} value={form.centro_reconocimiento_id}
                  onChange={(e) => set({ centro_reconocimiento_id: e.target.value, centro_reconocimiento_codigo: "" })}
                  data-testid="psico-centro">
                  <option value="">— No está en la lista / lo pongo manual —</option>
                  {centers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.recognition_center_number ? ` · ${c.recognition_center_number}` : ""}</option>
                  ))}
                </select>
              </Field>
              {!form.centro_reconocimiento_id && (
                <Field label="Código del centro (manual)">
                  <input className={inputCls + " font-mono"} value={form.centro_reconocimiento_codigo}
                    onChange={(e) => set({ centro_reconocimiento_codigo: e.target.value })}
                    placeholder="Solo el código — la administración completará el resto"
                    data-testid="psico-centro-codigo" />
                </Field>
              )}
            </div>

            <div className="card-surface p-4 space-y-3">
              <div className="text-sm font-bold">4 · Tu firma</div>
              <p className="text-xs text-neutral-500">Firma grande y clara, igual que en tu DNI — la usaremos para verificarla.</p>
              <BigSignatureCanvas onChange={setSignature} />
            </div>

            <button type="submit" disabled={submitting} data-testid="psico-submit"
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl text-sm font-semibold bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white transition-colors">
              <Send className="h-4 w-4" /> {submitting ? "Enviando…" : "Enviar solicitud"}
            </button>
          </form>
        )}
      </div>
      <Footer className="mt-4" />
    </div>
  );
}
